import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { specimenApi, apiError } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Button, Badge, Section, Modal, Field } from "../../Components/UI/ui";
import { GATE, TERMINAL, REJECT_REASONS, tubeName, fmtDateTime, normalizeSticker, toLocalDateTimeInput } from "../../lib/lab";

// Sample tracking with pre-printed stickers (specimens guide §2.2): the app
// lists the tubes a booking needs; the collector sticks a sticker on each tube
// and scans it. The first scan assigns the booking's accession number.
const SpecimenPanel = ({ appointment, onAppointmentChanged, onState, refreshKey }) => {
  const { can } = useAuth();
  const { toast } = useApp();
  const [data, setData] = useState(null);
  const [scans, setScans] = useState([]);          // queued { code, label, manual }
  const [target, setTarget] = useState("");        // tube the next scan goes to
  const [scanValue, setScanValue] = useState("");
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [manualFor, setManualFor] = useState(null); // tube code for manual entry
  const [manual, setManual] = useState({ a: "", b: "" });
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState(REJECT_REASONS[0]);
  const [reasonText, setReasonText] = useState("");
  const [recollecting, setRecollecting] = useState(null); // specimen being re-drawn
  const [recollectLabel, setRecollectLabel] = useState("");
  const isHome = appointment?.collectionType === "home";
  const [collectedAt, setCollectedAt] = useState("");
  const scanRef = useRef(null);
  const canCollect = can(...GATE.collectSpecimen) && !TERMINAL.has(appointment?.status) && appointment?.status !== "REPORT_READY";
  const canReject = can(...GATE.rejectSpecimen);

  const apply = useCallback((d) => {
    setData(d);
    onState?.(d);
    const next = (d.specimens || []).find((s) => s.status === "pending");
    setTarget(next?.code || "");
  }, [onState]);

  const load = useCallback(async () => {
    if (!appointment?._id) return;
    try { apply(await specimenApi.get(appointment._id)); setScans([]); setErr(null); }
    catch (e) { toast.error(e); }
  }, [appointment?._id, toast, apply]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const specimens = useMemo(() => data?.specimens || [], [data]);
  const pending = useMemo(() => specimens.filter((s) => s.status === "pending"), [specimens]);
  const queuedCodes = new Set(scans.map((s) => s.code));
  const nextTube = pending.find((s) => s.code === target) || pending.find((s) => !queuedCodes.has(s.code));

  // Queue a sticker for the highlighted tube; the next pending tube becomes the target
  const queue = (labelRaw, isManual = false) => {
    const label = normalizeSticker(labelRaw);
    setErr(null);
    if (!label) return;
    if (!/^[A-Z0-9]{4,16}$/.test(label)) return setErr(`"${labelRaw}" is not a sticker code`);
    const tube = nextTube;
    if (!tube) return setErr("Every tube already has a sticker queued or collected");
    if (scans.some((s) => s.label === label)) return setErr(`${label} is already queued for tube ${scans.find((s) => s.label === label).code}`);
    const nextScans = [...scans.filter((s) => s.code !== tube.code), { code: tube.code, label, manual: isManual }];
    setScans(nextScans);
    const used = new Set(nextScans.map((s) => s.code));
    setTarget(pending.find((s) => !used.has(s.code))?.code || "");
    setScanValue("");
    scanRef.current?.focus();
  };

  const submit = async () => {
    if (!scans.length) return;
    setBusy("collect"); setErr(null);
    try {
      const res = await specimenApi.collect(appointment._id, scans.map(({ code, label, manual: m }) => (m ? { code, label, manual: true } : { code, label })), isHome && collectedAt ? new Date(collectedAt).toISOString() : undefined);
      apply({ sampleId: res.sampleId, allCollected: res.allCollected, specimens: res.specimens });
      setScans([]);
      const n = res.collected?.length ?? scans.length;
      toast.success(`${n} tube${n === 1 ? "" : "s"} collected · Sample ID ${res.sampleId}${res.allCollected ? " · all tubes in" : ""}`);
      if (res.appointmentStatus && res.appointmentStatus !== appointment.status) onAppointmentChanged?.();
    } catch (e) {
      // All-or-nothing: show the server's message verbatim, keep the queue for correction
      setErr(apiError(e));
    } finally { setBusy(null); }
  };

  const reject = async () => {
    const r = reason === "other" ? reasonText.trim() : reason;
    if (!r) return toast.error("Give a reason");
    setBusy("reject");
    try {
      const res = await specimenApi.reject(appointment._id, rejecting.code, r);
      apply({ sampleId: res.sampleId ?? data.sampleId, allCollected: res.allCollected, specimens: res.specimens });
      setRejecting(null);
      toast.success(`Tube ${rejecting.code} rejected — its result sheets are blocked until a re-draw`);
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const recollect = async () => {
    const label = normalizeSticker(recollectLabel);
    if (!label) return;
    setBusy("recollect");
    try {
      const body = { label };
      if (isHome && collectedAt) body.collectedAt = new Date(collectedAt).toISOString();
      const res = await specimenApi.recollect(appointment._id, recollecting.code, body);
      apply({ sampleId: res.sampleId ?? data.sampleId, allCollected: res.allCollected, specimens: res.specimens });
      setRecollecting(null); setRecollectLabel("");
      toast.success(`Re-drawn as ${res.recollected?.code || "new tube"} · sticker ${res.recollected?.label || label}`);
      if (res.appointmentStatus && res.appointmentStatus !== appointment.status) onAppointmentChanged?.();
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  if (!data) return null;

  return (
    <Section
      title={
        <span className="lab-row">
          Sample
          {data.sampleId ? <span className="lab-sample-id">{data.sampleId}</span> : <span className="lab-muted" style={{ fontWeight: 400 }}>no sample yet</span>}
          {data.allCollected && <Badge kind="collected">All tubes collected</Badge>}
        </span>
      }
      right={canCollect && scans.length > 0 && (
        <Button size="sm" variant="primary" loading={busy === "collect"} onClick={submit}>Collect {scans.length} scanned tube{scans.length === 1 ? "" : "s"}</Button>
      )}
    >
      {specimens.length === 0 ? (
        <span className="lab-muted">No tubes — the ordered items have no test definitions, so nothing to collect.</span>
      ) : (
        <table className="lab-lines">
          <thead><tr><th>Tube</th><th>Specimen</th><th>Tests</th><th>Sticker</th><th>Status</th><th /></tr></thead>
          <tbody>
            {specimens.map((s) => {
              const queued = scans.find((x) => x.code === s.code);
              const isNext = canCollect && nextTube?.code === s.code && s.status === "pending" && !queued;
              return (
                <tr key={s.code} style={isNext ? { background: "#fffbea" } : s.status === "rejected" ? { opacity: .8 } : undefined}>
                  <td><span className={`lab-tube ${s.code.replace(/\d+$/, "")}`}>{s.code}</span></td>
                  <td>{s.specimenType || tubeName(s.code)}{s.containerType ? <span className="lab-muted"> · {s.containerType}</span> : null}</td>
                  <td className="lab-muted">{(s.testNames || []).join(", ")}</td>
                  <td>
                    {s.label ? <span className="lab-sample-id" style={{ fontSize: 14 }}>{s.label}</span>
                      : queued ? <span className="lab-row" style={{ gap: 6 }}><span className="lab-sample-id" style={{ fontSize: 14, color: "#7a4b00" }}>{queued.label}</span><Badge kind="IN_PROCESS">queued{queued.manual ? " · typed" : ""}</Badge><button type="button" className="lab-link" onClick={() => setScans(scans.filter((x) => x.code !== s.code))}>✕</button></span>
                      : <span className="lab-muted">—</span>}
                    {s.manualEntry && <div className="lab-muted" style={{ fontSize: 11 }}>typed manually</div>}
                  </td>
                  <td>
                    <Badge kind={s.status}>{s.status}</Badge>
                    {s.status === "collected" && <div className="lab-muted" style={{ fontSize: 11 }}>{fmtDateTime(s.collectedAt)}{s.collectedBy?.name ? ` · ${s.collectedBy.name}` : ""}</div>}
                    {s.status === "rejected" && <div className="lab-danger-text" style={{ fontSize: 11 }}>{s.rejection?.reason}{s.replacedBy ? ` · replaced by ${s.replacedBy}` : ""}</div>}
                  </td>
                  <td>
                    <span className="lab-row end" style={{ gap: 4 }}>
                      {s.status === "pending" && canCollect && !queued && nextTube?.code !== s.code && <Button size="xs" variant="ghost" onClick={() => setTarget(s.code)}>Scan this next</Button>}
                      {s.status === "collected" && canReject && appointment.status !== "COMPLETED" && (
                        <Button size="xs" variant="danger" onClick={() => { setRejecting(s); setReason(REJECT_REASONS[0]); setReasonText(""); }}>Reject</Button>
                      )}
                      {s.status === "rejected" && !s.replacedBy && canCollect && (
                        <Button size="xs" variant="navy" onClick={() => { setRecollecting(s); setRecollectLabel(""); }}>Re-collect</Button>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {canCollect && pending.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {nextTube ? (
            <div className="lab-row">
              <span style={{ fontSize: 14 }}>Stick a sticker on the <b>{nextTube.specimenType || tubeName(nextTube.code)}</b> tube <span className={`lab-tube ${nextTube.code.replace(/\d+$/, "")}`} style={{ margin: "0 4px" }}>{nextTube.code}</span> and scan it:</span>
              <input
                ref={scanRef}
                className="lab-input sm"
                style={{ width: 220, fontFamily: "ui-monospace, Menlo, monospace" }}
                placeholder="scan sticker…"
                value={scanValue}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setScanValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") queue(scanValue); }}
              />
              <Button size="sm" variant="ghost" onClick={() => { setManualFor(nextTube.code); setManual({ a: "", b: "" }); }}>Type code</Button>
            </div>
          ) : (
            <span className="lab-muted">All pending tubes have a sticker queued — press <b>Collect</b> to record them.</span>
          )}
          {isHome && (
            <Field label="Actual draw time (home collection)" hint="Leave blank to use now">
              <input type="datetime-local" className="lab-input sm" style={{ width: 260 }} value={collectedAt} max={toLocalDateTimeInput()} onChange={(e) => setCollectedAt(e.target.value)} />
            </Field>
          )}
        </div>
      )}
      {err && <div className="lab-danger-text" style={{ marginTop: 8 }}>{err}</div>}
      {!data.sampleId && specimens.length > 0 && <div className="lab-muted" style={{ marginTop: 8 }}>The sample ID is assigned by the first sticker scanned on this booking.</div>}

      {/* Manual entry: type the code twice (scanner broken) */}
      <Modal open={!!manualFor} onClose={() => setManualFor(null)} size="sm" title={`Type sticker code · tube ${manualFor || ""}`}
        footer={<><Button onClick={() => setManualFor(null)}>Cancel</Button><Button variant="primary" disabled={!manual.a || normalizeSticker(manual.a) !== normalizeSticker(manual.b)} onClick={() => { queue(manual.a, true); setManualFor(null); }}>Use this code</Button></>}>
        <div className="lab-muted">Only when the scanner isn't working. The entry is recorded as typed.</div>
        <Field label="Sticker code"><input className="lab-input w-full" style={{ fontFamily: "ui-monospace, Menlo, monospace" }} value={manual.a} onChange={(e) => setManual({ ...manual, a: e.target.value })} autoFocus /></Field>
        <Field label="Type it again" error={manual.b && normalizeSticker(manual.a) !== normalizeSticker(manual.b) ? "Codes don't match" : undefined}><input className="lab-input w-full" style={{ fontFamily: "ui-monospace, Menlo, monospace" }} value={manual.b} onChange={(e) => setManual({ ...manual, b: e.target.value })} /></Field>
      </Modal>

      <Modal open={!!rejecting} onClose={() => setRejecting(null)} size="sm" title={`Reject tube ${rejecting?.code || ""}`}
        footer={<><Button onClick={() => setRejecting(null)}>Cancel</Button><Button variant="danger" loading={busy === "reject"} onClick={reject}>Reject tube</Button></>}>
        <div className="lab-muted">Sheets for {(rejecting?.testNames || []).join(", ")} will refuse entry and verification until the tube is re-drawn with a new sticker.</div>
        <Field label="Reason">
          <select className="lab-select w-full" value={reason} onChange={(e) => setReason(e.target.value)}>
            {REJECT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            <option value="other">other…</option>
          </select>
        </Field>
        {reason === "other" && <input className="lab-input w-full" placeholder="Describe the problem" value={reasonText} onChange={(e) => setReasonText(e.target.value)} autoFocus />}
      </Modal>

      <Modal open={!!recollecting} onClose={() => setRecollecting(null)} size="sm" title={`Re-draw tube ${recollecting?.code || ""}`}
        footer={<><Button onClick={() => setRecollecting(null)}>Cancel</Button><Button variant="primary" loading={busy === "recollect"} disabled={!normalizeSticker(recollectLabel)} onClick={recollect}>Record re-draw</Button></>}>
        <div className="lab-muted">Draw a fresh <b>{recollecting?.specimenType || tubeName(recollecting?.code)}</b> tube, stick a <b>new</b> sticker on it and scan it. The old sticker stays used.</div>
        <Field label="New sticker"><input className="lab-input w-full" style={{ fontFamily: "ui-monospace, Menlo, monospace" }} placeholder="scan sticker…" value={recollectLabel} autoFocus onChange={(e) => setRecollectLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && recollect()} /></Field>
      </Modal>
    </Section>
  );
};

export default SpecimenPanel;
